import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Text from '../../components/common/Text';
import { useTheme } from '../../hooks/useTheme';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { Ionicons } from '@expo/vector-icons';
import AppToolbar from '../../components/shared/AppToolbar';
import { API_URL } from '../../constants/config';

interface FAQ {
  id: number;
  question: string;
  answer: string;
  category?: string;
  order: number;
  views_count: number;
  helpful_count: number;
  not_helpful_count: number;
  created_at: string;
  updated_at: string;
}

// Default FAQ items from Settings
const defaultFAQs: FAQ[] = [
  {
    id: 1,
    question: 'How to get started with colour analysis?',
    answer:
      'To get started with colour analysis, take a photo of yourself in natural lighting. Our AI will analyze your skin tone, undertones, and features to determine your perfect colour palette.',
    category: 'general',
    order: 1,
    views_count: 0,
    helpful_count: 0,
    not_helpful_count: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 2,
    question: 'Troubleshooting analysis issues',
    answer:
      "If you're experiencing issues with colour analysis, make sure you're in good natural lighting, your face is clearly visible, and you're not wearing heavy makeup. Try taking the photo in different lighting conditions. If problems persist, contact support@mail.mycoloursense.com for assistance.",
    category: 'general',
    order: 2,
    views_count: 0,
    helpful_count: 0,
    not_helpful_count: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  // {
  //   id: 3,
  //   question: "Managing wardrobe items",
  //   answer: "You can add items to your wardrobe by taking photos or uploading images. Organize items by tags, colors, or categories. Use the smart wardrobe feature to get outfit suggestions based on your colour profile and existing items.",
  //   category: "general",
  //   order: 3,
  //   views_count: 0,
  //   helpful_count: 0,
  //   not_helpful_count: 0,
  //   created_at: new Date().toISOString(),
  //   updated_at: new Date().toISOString(),
  // },
  // Moodboard FAQ hidden for now
  // {
  //   id: 4,
  //   question: "Creating mood boards",
  //   answer: "To create a mood board, go to the Mood Boards section and tap the '+' button. You can add photos, organize them by theme or colour palette, and share them with others. Mood boards help you visualize your style and plan outfits.",
  //   category: "general",
  //   order: 4,
  //   views_count: 0,
  //   helpful_count: 0,
  //   not_helpful_count: 0,
  //   created_at: new Date().toISOString(),
  //   updated_at: new Date().toISOString(),
  // },
  {
    id: 5,
    question: 'How do I contact support?',
    answer:
      "You can contact our support team at support@mail.mycoloursense.com. We typically respond within 24 hours. Support hours are Monday-Friday, 9 AM - 6 PM EST. For urgent issues, please include 'URGENT' in your subject line.",
    category: 'support',
    order: 5,
    views_count: 0,
    helpful_count: 0,
    not_helpful_count: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  // {
  //   id: 6,
  //   question: "How do I cancel my subscription?",
  //   answer: "You can cancel your subscription at any time by going to Settings > Billing & Subscription > Cancel Subscription. Your access will continue until the end of your current billing period.",
  //   category: "subscription",
  //   order: 6,
  //   views_count: 0,
  //   helpful_count: 0,
  //   not_helpful_count: 0,
  //   created_at: new Date().toISOString(),
  //   updated_at: new Date().toISOString(),
  // },
  // {
  //   id: 7,
  //   question: "Can I get a refund?",
  //   answer: "We offer refunds within 7 days of purchase for new subscriptions. Please contact our support team with your order details for assistance.",
  //   category: "billing",
  //   order: 7,
  //   views_count: 0,
  //   helpful_count: 0,
  //   not_helpful_count: 0,
  //   created_at: new Date().toISOString(),
  //   updated_at: new Date().toISOString(),
  // },
  // {
  //   id: 8,
  //   question: "How do I update my payment method?",
  //   answer: "Go to Settings > Billing & Subscription > Manage Billing to update your payment information. You can change your card details or add a new payment method.",
  //   category: "payment",
  //   order: 8,
  //   views_count: 0,
  //   helpful_count: 0,
  //   not_helpful_count: 0,
  //   created_at: new Date().toISOString(),
  //   updated_at: new Date().toISOString(),
  // },
  // {
  //   id: 9,
  //   question: "How do I start a free trial?",
  //   answer: "You can start a 7-day free trial by going to Settings > Billing & Subscription and selecting 'Start Free Trial'. No payment is required during the trial period.",
  //   category: "subscription",
  //   order: 9,
  //   views_count: 0,
  //   helpful_count: 0,
  //   not_helpful_count: 0,
  //   created_at: new Date().toISOString(),
  //   updated_at: new Date().toISOString(),
  // },
  // {
  //   id: 10,
  //   question: "What happens when my trial ends?",
  //   answer: "When your trial ends, you'll be automatically charged for the Pro plan unless you cancel before the trial expires. You can cancel anytime during the trial period.",
  //   category: "subscription",
  //   order: 10,
  //   views_count: 0,
  //   helpful_count: 0,
  //   not_helpful_count: 0,
  //   created_at: new Date().toISOString(),
  //   updated_at: new Date().toISOString(),
  // },
];

const FAQScreen = () => {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { isMobile } = useResponsiveLayout();
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Set meta description and Open Graph tags for web
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute(
        'content',
        'Frequently Asked Questions about ColourSense - Get answers to common questions about colour analysis, virtual draping, mood boards, and more.'
      );

      // Update Open Graph meta tags
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute('content', 'ColourSense | FAQ');
      }

      const ogDescription = document.querySelector(
        'meta[property="og:description"]'
      );
      if (ogDescription) {
        ogDescription.setAttribute(
          'content',
          'Frequently Asked Questions about ColourSense - Get answers to common questions about colour analysis, virtual draping, mood boards, and more.'
        );
      }
    }
  }, []);

  const fetchFAQs = useCallback(async () => {
    try {
      setLoading(true);
      const url = selectedCategory
        ? `${API_URL}/feedback/faqs?category=${selectedCategory}`
        : `${API_URL}/feedback/faqs`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        // Use API data if available, otherwise use default FAQs
        setFaqs(data.length > 0 ? data : defaultFAQs);
      } else {
        // If API fails, use default FAQs
        setFaqs(defaultFAQs);
      }
    } catch {
      // If API fails, use default FAQs
      setFaqs(defaultFAQs);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    void fetchFAQs();
  }, [fetchFAQs]);

  const toggleFAQ = (id: number) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  // Get unique categories
  const categories = Array.from(
    new Set(faqs.map((faq) => faq.category).filter(Boolean))
  );

  // Helper function to convert category to title case
  const toTitleCase = (str: string) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Filter FAQs by category
  const filteredFAQs = selectedCategory
    ? faqs.filter((faq) => faq.category === selectedCategory)
    : faqs;

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <AppToolbar
        showBackButton={false}
        title="Frequently Asked Questions"
        showProfile={false}
        showSettings={false}
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.contentContainer,
          { paddingBottom: insets.bottom + 20 },
        ]}
      >
        <View style={[styles.content, isMobile && styles.contentMobile]}>
          {/* Category Filter */}
          {categories.length > 0 && (
            <View style={styles.categoryContainer}>
              <TouchableOpacity
                style={[
                  styles.categoryButton,
                  {
                    backgroundColor:
                      selectedCategory === null ? colors.primary : colors.card,
                    borderColor: colors.border,
                  },
                ]}
                onPress={() => setSelectedCategory(null)}
              >
                <Text
                  style={[
                    styles.categoryButtonText,
                    { color: selectedCategory === null ? '#fff' : colors.text },
                  ]}
                >
                  All
                </Text>
              </TouchableOpacity>
              {categories.map((category) => (
                <TouchableOpacity
                  key={category}
                  style={[
                    styles.categoryButton,
                    {
                      backgroundColor:
                        selectedCategory === category
                          ? colors.primary
                          : colors.card,
                      borderColor: colors.border,
                    },
                  ]}
                  onPress={() => setSelectedCategory(category ?? null)}
                >
                  <Text
                    style={[
                      styles.categoryButtonText,
                      {
                        color:
                          selectedCategory === category ? '#fff' : colors.text,
                      },
                    ]}
                  >
                    {category ? toTitleCase(category) : ''}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}

          {/* FAQs List */}
          {loading ? (
            <Text style={[styles.loading, { color: colors.secondary }]}>
              Loading FAQs...
            </Text>
          ) : filteredFAQs.length === 0 ? (
            <Text style={[styles.empty, { color: colors.secondary }]}>
              No FAQs found
            </Text>
          ) : (
            <View style={styles.faqList}>
              {filteredFAQs.map((faq) => {
                const isExpanded = expandedIds.has(faq.id);
                return (
                  <View
                    key={faq.id}
                    style={[
                      styles.faqItem,
                      {
                        backgroundColor: colors.card,
                        borderColor: colors.border,
                      },
                    ]}
                  >
                    <TouchableOpacity
                      style={styles.faqQuestion}
                      onPress={() => toggleFAQ(faq.id)}
                      activeOpacity={0.7}
                    >
                      <Text
                        style={[styles.faqQuestionText, { color: colors.text }]}
                      >
                        {faq.question}
                      </Text>
                      <Ionicons
                        name={isExpanded ? 'chevron-up' : 'chevron-down'}
                        size={20}
                        color={colors.text}
                      />
                    </TouchableOpacity>
                    {isExpanded && (
                      <View style={styles.faqAnswer}>
                        <Text
                          style={[
                            styles.faqAnswerText,
                            { color: colors.secondary },
                          ]}
                        >
                          {faq.answer}
                        </Text>
                        {faq.category && (
                          <Text
                            style={[
                              styles.faqCategory,
                              { color: colors.secondary },
                            ]}
                          >
                            Category: {toTitleCase(faq.category)}
                          </Text>
                        )}
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
  },
  content: {
    maxWidth: 800,
    alignSelf: 'center',
    width: '100%',
  },
  contentMobile: {
    paddingHorizontal: 16,
  },
  title: {
    fontSize: 32,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 32,
    textAlign: 'center',
  },
  categoryContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 24,
  },
  categoryButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
  },
  categoryButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  loading: {
    textAlign: 'center',
    marginTop: 20,
    fontSize: 14,
  },
  empty: {
    textAlign: 'center',
    marginTop: 20,
    fontSize: 14,
  },
  faqList: {
    gap: 12,
  },
  faqItem: {
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
  },
  faqQuestion: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  faqQuestionText: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
    marginRight: 12,
  },
  faqAnswer: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  faqAnswerText: {
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 8,
  },
  faqCategory: {
    fontSize: 12,
    fontStyle: 'italic',
  },
});

export default FAQScreen;
